import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { fetchJob } from '~/actions';
import { Button, Center, Column, ConfirmPopup, Row, Text } from '~/commonComponents';
import browserHistory from '~/history';
import { useAppSelector } from '~/hooks';
import checkedCircleIcon from '~/images/checkedCircleIcon.svg';
import uncheckedCircleIcon from '~/images/uncheckedCircleIcon.svg';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import { Colors } from '~/themes/colors';
import AnimatedCheckCircle from '../shared/AnimatedCheckCircle';
import { IPermCancellationEventProps, IShiftCancellationEventProps } from './Types';
import { useCancellationReasons } from './useCancellationReasons';

const SuccessIconComponent = () => <AnimatedCheckCircle />;

export const CancellationFeedback = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { jobId } = params;

  const [cancellationFeedback, setCancellationFeedback] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');

  const currentJob = useAppSelector((state) => state.job?.job);
  const { options } = useCancellationReasons({ isOnsiteInterview: currentJob?.isOnsiteInterview });

  const isFormInvalid =
    cancellationFeedback === '' ||
    (cancellationFeedback === 'Other (please specify)' && cancellationReason?.length < 3);

  useEffect(() => {
    if (jobId) {
      dispatch(fetchJob({ jobId }));
    }
  }, [dispatch, jobId]);

  const onSubmitFeedback = useCallback(() => {
    if (currentJob?.isOnsiteInterview) {
      trackPosthogEvent(POSTHOG_EVENTS.OFFICE_PERM_INTERVIEW_CANCELLATION_REASON_SUBMITTED, {
        officeId: currentJob?.offer_owner?.id,
        onsiteInterviewId: currentJob?.id,
        postingId: currentJob?.postingId,
        reason: cancellationFeedback,
        othersReason: cancellationReason,
      } satisfies IPermCancellationEventProps);
    } else {
      trackPosthogEvent(POSTHOG_EVENTS.OFFICE_SHIFT_CANCELLATION_REASON_SUBMITTED, {
        officeId: currentJob?.offer_owner?.id,
        shiftId: currentJob?.id,
        reason: cancellationFeedback,
        othersReason: cancellationReason,
      } satisfies IShiftCancellationEventProps);
    }
    browserHistory.push('/dashboard/home');
  }, [
    cancellationFeedback,
    cancellationReason,
    currentJob?.id,
    currentJob?.isOnsiteInterview,
    currentJob?.offer_owner?.id,
    currentJob?.postingId,
  ]);

  return (
    // @ts-expect-error - ConfirmPopup still written in JavaScript
    <ConfirmPopup
      Icon={SuccessIconComponent}
      title="Cancellation Successful!"
      titleStyle={{ marginTop: 20 }}
      description={
        currentJob?.isOnsiteInterview ? (
          <Text color={Colors.neutral_500} fontSize={18} textAlign="center">
            Help us improve your experience by sharing
            <br />
            why this onsite interview was cancelled.
            <br />
            Please select one option below.
          </Text>
        ) : (
          <Text color={Colors.neutral_500} fontSize={18} textAlign="center">
            Help us improve your experience by sharing
            <br />
            why this shift was cancelled. Please select
            <br />
            one option below.
          </Text>
        )
      }
      descriptionStyle={{ marginTop: 16, marginBottom: 26 }}
    >
      <Column gap={18} width="75%" marginBottom={38}>
        {options.map((it) => (
          <Row
            alignItems="center"
            cursor="pointer"
            data-cy={`cancellation-reason-${it}`}
            gap={10}
            key={it}
            paddingInline={16}
            width="100%"
            onClick={() => {
              setCancellationFeedback(it);
              setCancellationReason('');
            }}
          >
            <img
              src={cancellationFeedback === it ? checkedCircleIcon : uncheckedCircleIcon}
              alt={cancellationFeedback === it ? 'Selected' : 'Unselected'}
            />
            <Text cursor="pointer">{it}</Text>
          </Row>
        ))}
      </Column>

      <motion.div
        initial="hide"
        animate={cancellationFeedback.includes('Other') ? 'show' : 'hide'}
        variants={textAreaVariants}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <textarea
          data-cy="cancellation-details-textarea"
          className="textarea-style"
          placeholder="Provide details here..."
          style={{
            width: '80%',
            height: 60,
            fontSize: 16,
            marginBottom: 20,
          }}
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
        />
      </motion.div>
      <Center>
        <Button
          dataCy="submit-cancellation-feedback"
          disabled={isFormInvalid}
          text="Submit"
          color="primary"
          style={{ width: 150 }}
          onClick={onSubmitFeedback}
        />
      </Center>
    </ConfirmPopup>
  );
};

const textAreaVariants = {
  show: {
    maxHeight: 130,
    opacity: 1,
    display: 'flex',
  },
  hide: {
    maxHeight: 0,
    opacity: 0,
    transitionEnd: { display: 'none' },
  },
};
